import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { formatCurrency } from "@/utils/currency";

type Props = {
  basePrice: number;
  baseDiscount: number;
  tourPrice?: number;
  promoCodeDiscount?: number;
  totalPrice: number;
};

export function PriceDetailsCard({
  basePrice,
  baseDiscount,
  promoCodeDiscount,
  tourPrice,
  totalPrice,
}: Props) {
  const intl = useIntl();

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        defaultMessage: "Price details",
        description:
          "Section heading for the price breakdown on the trip booking summary page",
      })}
      collapsible={false}
    >
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage: "Base trip price",
            description: "Label for the base trip price before optional extras",
          })}
        </dt>
        <dd className="text-end font-bold">{formatCurrency(basePrice)}</dd>

        {tourPrice !== undefined && (
          <>
            <dt className="text-text-disabled-primary">
              {intl.formatMessage({
                defaultMessage: "City tours",
                description:
                  "Label for the per-person price of an optional tour",
              })}
            </dt>
            <dd className="text-end font-bold">{formatCurrency(tourPrice)}</dd>
          </>
        )}

        <dt className="text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage: "Discount",
            description:
              "Label for a discount applied to the total trip price in the price breakdown",
          })}
        </dt>
        <dd className="text-end font-bold">{formatCurrency(baseDiscount)}</dd>

        {promoCodeDiscount ? (
          <>
            <dt className="text-text-disabled-primary">
              {intl.formatMessage({
                defaultMessage: "Promo code discount",
                description:
                  "Line item label for the promo code discount in the price summary",
              })}
            </dt>
            <dd className="text-end font-bold text-core-accent-500">
              {formatCurrency(promoCodeDiscount)}
            </dd>
          </>
        ) : null}

        <dt className="text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage: "Payment method",
            description:
              "Label for the selected payment method shown in the booking summary",
          })}
        </dt>
        <dd className="text-end font-bold">
          {intl.formatMessage({
            defaultMessage: "Qi Card",
            description: "Payment method label for the QI card",
          })}
        </dd>

        <dt className="text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage: "Total price",
            description: "Label for the total booking price",
          })}
        </dt>
        <dd className="text-end font-bold">{formatCurrency(totalPrice)}</dd>
      </dl>
    </CollapsibleCard>
  );
}
